import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, History, RotateCcw, ChevronDown, ChevronUp,
  Clock, Loader2, CheckCircle2, Eye, ArrowRight, GitCompare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ConfirmDialog';
import { API } from './helpers';
import BulkSnapshotPreview from './BulkSnapshotPreview';
import DataGridDiff from './DataGridDiff';

const NB_M = ['jan','feb','mar','apr','mai','jun','jul','aug','sep','okt','nov','des'];
const fmtDate = (s) => { const d = new Date(s); return isNaN(d) ? '' : `${d.getDate()}. ${NB_M[d.getMonth()]} ${d.getFullYear()}, kl. ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
const relTime = (s) => {
  const diff = Date.now() - new Date(s).getTime();
  if (diff < 60000) return 'Akkurat no';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min sidan`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} timar sidan`;
  return fmtDate(s);
};

export function VersionHistoryModal({ bulkId, bulkLabel, onClose, onRestore }) {
  const confirmDialog = useConfirm();
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [versionDetails, setVersionDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const fetchVersions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bulk-registrations/${bulkId}/versions`, { withCredentials: true });
      setVersions(res.data.versions || []);
      setCurrentVersion(res.data.current_version || 1);
    } catch { toast.error('Kunne ikkje hente versjonshistorikk'); }
    finally { setLoading(false); }
  }, [bulkId]);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  const fetchVersionDetail = async (versionNum) => {
    if (versionDetails[versionNum]) return versionDetails[versionNum];
    setLoadingDetail(versionNum);
    try {
      const res = await axios.get(`${API}/bulk-registrations/${bulkId}/versions/${versionNum}`, { withCredentials: true });
      setVersionDetails(prev => ({ ...prev, [versionNum]: res.data }));
      return res.data;
    } catch { toast.error('Kunne ikkje hente detaljar'); return null; }
    finally { setLoadingDetail(null); }
  };

  const toggleExpand = (versionNum) => {
    if (expandedVersion === versionNum) { setExpandedVersion(null); }
    else { setExpandedVersion(versionNum); fetchVersionDetail(versionNum); }
  };

  const handlePreview = async (versionNum) => {
    let detail = versionDetails[versionNum];
    if (!detail) detail = await fetchVersionDetail(versionNum);
    if (detail?.snapshot) setPreviewData({ version: versionNum, ...detail });
  };

  const handleRestore = async (versionNum) => {
    const ok = await confirmDialog({ title: 'Gjenopprett versjon?', message: `Gjenopprette til v${versionNum}? Gjeldande data vert lagra først.`, confirmText: 'Ja, gjenopprett', variant: 'warning' });
    if (!ok) return;
    setRestoring(versionNum);
    try {
      await axios.post(`${API}/bulk-registrations/${bulkId}/restore/${versionNum}`, {}, { withCredentials: true });
      toast.success('Versjon gjenoppretta');
      fetchVersions(); setExpandedVersion(null); setVersionDetails({}); setPreviewData(null);
      if (onRestore) onRestore();
    } catch { toast.error('Kunne ikkje gjenopprette'); }
    finally { setRestoring(null); }
  };

  const formatFieldName = (f) => ({ 'date': 'Dato', 'area_name': 'Område', 'biologist': 'Biolog', 'sample_count': 'Stasjonar', 'algae_data': 'Algedata', 'zooplankton_data': 'Zooplanktondata', 'color_overrides': 'Fargekoder', 'temperature_data': 'Temperatur', 'ai_summary': 'Sammendrag' }[f] || f);

  // If preview is active, show fullscreen preview
  if (previewData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setPreviewData(null)}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-3">
              <button onClick={() => setPreviewData(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-900">Versjon {previewData.version}</h3>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">v{previewData.version}</span>
                </div>
                <p className="text-xs text-slate-500">{previewData.edited_by_email} · {fmtDate(previewData.saved_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreviewData(null)}>Tilbake</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => { setPreviewData(null); handleRestore(previewData.version); }}>
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Gjenopprett
              </Button>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
            <BulkSnapshotPreview snapshot={previewData.snapshot} versionLabel={`v${previewData.version}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="version-history-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <History className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Versjonshistorikk</h3>
              <p className="text-xs text-slate-500">{bulkLabel || bulkId}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full" data-testid="current-version-badge">
              Gjeldande: v{currentVersion}
            </span>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mr-3" />
              <span className="text-sm text-slate-500">Lastar...</span>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-16">
              <History className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Ingen tidlegare versjonar</p>
              <p className="text-sm text-slate-400">Versjonar vert oppretta når data vert endra</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Current version */}
              <div className="flex items-center gap-3 px-3 py-2 bg-indigo-50 rounded-xl border border-indigo-100">
                <CheckCircle2 className="w-5 h-5 text-indigo-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-indigo-700">Gjeldande versjon (v{currentVersion})</p>
                  <p className="text-xs text-indigo-500">Aktiv no</p>
                </div>
              </div>

              {/* Past versions */}
              {versions.map((v) => {
                const isExpanded = expandedVersion === v.version;
                const detail = versionDetails[v.version];
                const isLoading = loadingDetail === v.version;

                return (
                  <div key={v.version} className={`rounded-xl border transition-all ${isExpanded ? 'border-slate-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`version-entry-${v.version}`}>
                    {/* Collapsed header */}
                    <button onClick={() => toggleExpand(v.version)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`version-toggle-${v.version}`}>
                      <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded font-mono shrink-0">v{v.version}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{v.edited_by_email}</p>
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Clock className="w-3 h-3" /> {relTime(v.saved_at)}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 px-4 py-3">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400 mr-2" /><span className="text-sm text-slate-400">Hentar...</span></div>
                        ) : detail ? (
                          <>
                            {/* Changes */}
                            {detail.changes?.length > 0 ? (
                              <div className="mb-3">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Endringar</p>
                                <div className="space-y-1">
                                  {detail.changes.filter(ch => !['Algedata', 'Zooplanktondata'].includes(ch.field) && !ch.field.includes(':')).slice(0, 8).map((ch, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs">
                                      <span className="font-medium text-slate-600 min-w-[80px] shrink-0">{formatFieldName(ch.field)}</span>
                                      {ch.to ? (
                                        <>
                                          <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[11px] truncate max-w-[120px]">{ch.from}</span>
                                          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                                          <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[11px] truncate max-w-[120px]">{ch.to}</span>
                                        </>
                                      ) : (
                                        <span className="text-slate-500">{ch.from}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400 italic mb-3">Ingen feltendringer registrert</p>
                            )}

                            {/* Visual Grid Diff */}
                            {detail.grid_diff && Object.keys(detail.grid_diff).length > 0 && (
                              <div className="mb-3" data-testid="visual-grid-diff">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <GitCompare className="w-3.5 h-3.5 text-indigo-500" />
                                  <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Celle-endringar i datatabellar</p>
                                </div>
                                {detail.grid_diff.algae_data && detail.snapshot?.algae_data && (
                                  <DataGridDiff
                                    data={detail.snapshot.algae_data}
                                    gridDiff={detail.grid_diff.algae_data}
                                    colorOverrides={detail.snapshot?.color_overrides}
                                    tempData={detail.snapshot?.temperature_data}
                                    title="Algedata"
                                    unit="ant/l"
                                  />
                                )}
                                {detail.grid_diff.zooplankton_data && detail.snapshot?.zooplankton_data && (
                                  <DataGridDiff
                                    data={detail.snapshot.zooplankton_data}
                                    gridDiff={detail.grid_diff.zooplankton_data}
                                    colorOverrides={detail.snapshot?.color_overrides}
                                    tempData={detail.snapshot?.temperature_data}
                                    title="Zooplanktondata"
                                    unit="ant/m³"
                                  />
                                )}
                                {/* Legend */}
                                <div className="flex items-center gap-4 text-[10px] text-slate-500 mt-1.5">
                                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300 inline-block" /> Endra celle</span>
                                  <span className="flex items-center gap-1"><span className="text-red-500 line-through">123</span> Gammal verdi</span>
                                  <span className="flex items-center gap-1"><span className="text-emerald-600 font-semibold">456</span> Ny verdi</span>
                                </div>
                              </div>
                            )}

                            {/* Snapshot info */}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-3 py-2 px-3 bg-slate-50 rounded-lg">
                              {detail.snapshot?.date && <span>Dato: <strong>{detail.snapshot.date}</strong></span>}
                              {detail.snapshot?.area_name && <span>Område: <strong>{detail.snapshot.area_name}</strong></span>}
                              {detail.snapshot?.biologist && <span>Biolog: <strong>{detail.snapshot.biologist}</strong></span>}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handlePreview(v.version)} data-testid={`preview-btn-${v.version}`}>
                                <Eye className="w-3.5 h-3.5 mr-1.5" /> Vis skjema
                              </Button>
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                                onClick={() => handleRestore(v.version)} disabled={restoring === v.version} data-testid={`restore-btn-${v.version}`}>
                                {restoring === v.version ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                                Gjenopprett
                              </Button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50 shrink-0">
          <p className="text-xs text-slate-400">{versions.length} tidlegare versjonar</p>
          <Button variant="outline" size="sm" onClick={onClose}>Lukk</Button>
        </div>
      </div>
    </div>
  );
}
